import { useCallback, useEffect, useRef, useState } from "react";
import { useTutorialDemo } from "../hooks/useTutorialDemo";
import { STEPS } from "../constants/tutorial-steps";
import { TutorialGuidedStep } from "./TutorialGuidedStep";
import { TutorialModalStep } from "./TutorialModalStep";

interface TutorialModalProps {
  readonly onClose: () => void;
  readonly onNavigate: (path: string) => void;
}

export function TutorialModal({ onClose, onNavigate }: TutorialModalProps) {
  const [step, setStep] = useState(0);
  const [validated, setValidated] = useState(false);
  const demo = useTutorialDemo();
  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;
  const autoAdvanceTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const complete = useCallback(async () => {
    demo.cleanup();
    localStorage.setItem("palnia_tutorial_completed", "true");
    // Marquer comme terminé côté serveur
    try {
      await fetch("/api/users/me", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ tutorialCompleted: true }),
      });
    } catch {
      // Silencer l'erreur - le localStorage sert de fallback
    }
    onClose();
  }, [demo, onClose]);

  const goToStep = useCallback(
    (next: number) => {
      setValidated(false);
      setStep(next);
    },
    [],
  );

  useEffect(() => {
    switch (step) {
      case 1:
        demo.injectTasks();
        onNavigate("/today");
        break;
      case 2:
        onNavigate("/tasks");
        break;
      case 3:
        demo.injectEvents();
        onNavigate("/calendar");
        break;
      case 4:
        onNavigate("/stats");
        break;
      case 5:
        onNavigate("/today");
        break;
    }
  }, [step, demo, onNavigate]);

  useEffect(() => {
    if (validated) return;
    const v = current.validate;
    if (!v) return;

    const success =
      (v === "task-status" && demo.hasDemoTaskStatusChanged) ||
      (v === "task-created" && demo.hasNewTaskBeenCreated) ||
      (v === "event-created" && demo.hasNewEventBeenCreated);

    if (success) {
      autoAdvanceTimer.current = setTimeout(() => {
        setValidated(true);
        setTimeout(() => goToStep(step + 1), 1200);
      }, 0);
    }

    return () => clearTimeout(autoAdvanceTimer.current);
  }, [
    validated,
    current.validate,
    demo.hasDemoTaskStatusChanged,
    demo.hasNewTaskBeenCreated,
    demo.hasNewEventBeenCreated,
    step,
    goToStep,
  ]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") complete();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [complete]);

  const prev = () => goToStep(Math.max(0, step - 1));
  const next = () => (isLast ? complete() : goToStep(step + 1));

  if (current.mode === "guided") {
    return (
      <TutorialGuidedStep
        current={current}
        step={step}
        validated={validated}
        isLast={isLast}
        onPrev={prev}
        onNext={next}
        onSkip={complete}
      />
    );
  }

  return (
    <TutorialModalStep
      current={current}
      step={step}
      onPrev={prev}
      onNext={next}
      onSkip={complete}
    />
  );
}
